import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import {
  Mic, Play, Pause, Save, Loader2, RefreshCw, Sliders,
  Volume2, Wand2, UserCheck, Trash2, Zap, Square
} from "lucide-react";
import { Slider } from "../components/ui/slider";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ─── Voice Blend Defaults & Presets (from VoiceBlendControls) ───
const BLEND_DEFAULTS = {
  formantShift: 0, brightness: 0, bodyWeight: 0,
  speed: 1.0, emphasis: 0,
  clarity: 0, sibilance: 0,
  pitch: 0,
  breathiness: 0, raspiness: 0,
  compression: 0, volume: 100,
};

const BLEND_PRESETS = [
  { id: "raspy", label: "Raspy", color: "#FF6B6B",
    values: { ...BLEND_DEFAULTS, raspiness: 55, bodyWeight: 3, brightness: -2, clarity: 4, compression: 30, volume: 105 } },
  { id: "gravelly", label: "Gravelly", color: "#A78BFA",
    values: { ...BLEND_DEFAULTS, raspiness: 40, bodyWeight: 8, formantShift: -4, pitch: -2, brightness: -4, compression: 45, volume: 110 } },
  { id: "screechy", label: "Screechy", color: "#F59E0B",
    values: { ...BLEND_DEFAULTS, formantShift: 6, brightness: 8, sibilance: 6, clarity: 8, raspiness: 25, pitch: 3, volume: 90 } },
  { id: "comedic", label: "Comedic", color: "#1ABC9C",
    values: { ...BLEND_DEFAULTS, formantShift: 8, pitch: 5, speed: 1.15, brightness: 4, emphasis: 60, clarity: 6, volume: 110 } },
  { id: "smooth", label: "Smooth", color: "#38BDF8",
    values: { ...BLEND_DEFAULTS, bodyWeight: 5, brightness: -3, clarity: 3, sibilance: -4, compression: 50, breathiness: 10, volume: 105 } },
  { id: "whisper", label: "Whisper", color: "#EC4899",
    values: { ...BLEND_DEFAULTS, breathiness: 50, volume: 85, brightness: -4, bodyWeight: -2, compression: 15, clarity: -3, speed: 0.92 } },
  { id: "villain", label: "Villain", color: "#9D00FF",
    values: { ...BLEND_DEFAULTS, pitch: -4, formantShift: -6, bodyWeight: 10, raspiness: 20, brightness: -5, compression: 60, emphasis: 50, volume: 115 } },
  { id: "robotic", label: "Robotic", color: "#6EE7B7",
    values: { ...BLEND_DEFAULTS, formantShift: 3, clarity: 10, sibilance: 4, compression: 80, breathiness: 0, raspiness: 15, emphasis: 80, brightness: 3 } },
  { id: "breathe", label: "Breathe", color: "#FB923C",
    values: { ...BLEND_DEFAULTS, breathiness: 35, volume: 90, brightness: -2, bodyWeight: 2, compression: 10, clarity: 2, speed: 0.95 } },
];

// ─── Slider component ───
const Knob = ({ label, value, onChange, min, max, step, unit, color }) => (
  <div>
    <div className="flex justify-between items-center mb-1.5">
      <span className="text-white/55 text-[11px]">{label}</span>
      <span className="text-[11px] font-bold tabular-nums" style={{ color }}>
        {value > 0 && (unit === "dB" || unit === "st") ? "+" : ""}{typeof value === "number" ? (Number.isInteger(value) ? value : value.toFixed(2)) : value}{unit}
      </span>
    </div>
    <Slider value={[value]} onValueChange={v => onChange(v[0])} min={min} max={max} step={step} className="w-full" />
  </div>
);

const Section = ({ title, color, children }) => (
  <div className="rounded-xl bg-white/[0.02] border border-white/5 p-3 space-y-3">
    <span className="text-[10px] font-bold uppercase tracking-wider block" style={{ color }}>{title}</span>
    {children}
  </div>
);

export default function VoiceLab({ series }) {
  const [presets, setPresets] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);

  // Profile fields
  const [editing, setEditing] = useState(null);
  const [profileName, setProfileName] = useState("");
  const [selectedDialect, setSelectedDialect] = useState("");
  const [gender, setGender] = useState("male");
  const [baseVoice, setBaseVoice] = useState("ash");
  const [octaveRange, setOctaveRange] = useState("baritone");
  const [selectedRules, setSelectedRules] = useState([]);
  const [emotionPreset, setEmotionPreset] = useState("neutral_professional");
  const [selectedBlendPreset, setSelectedBlendPreset] = useState("");
  const [sampleText, setSampleText] = useState("The ink never lies. Every signature tells a story, and this one was written in blood.");

  // Voice Blend params (all 12)
  const [blend, setBlend] = useState({ ...BLEND_DEFAULTS });

  // Preview — Web Audio API processing
  const [previewing, setPreviewing] = useState(false);
  const [transformedText, setTransformedText] = useState("");
  const [playingProfile, setPlayingProfile] = useState(null);
  const audioCtxRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const noiseNodeRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [matchedChar, setMatchedChar] = useState(null);
  const [previewAudioUrl, setPreviewAudioUrl] = useState(null);
  const [decodedBuffer, setDecodedBuffer] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/voice-lab/presets`),
      axios.get(`${API}/voice-lab/profiles?series=${series || ""}`),
      axios.get(`${API}/characters?series=${series || "the-red-ink-chronicles"}`),
    ]).then(([p, pr, ch]) => {
      setPresets(p.data);
      setProfiles(pr.data);
      setCharacters(ch.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, [series]);

  // Auto-match profile name to character
  useEffect(() => {
    if (!profileName.trim() || !characters.length) { setMatchedChar(null); return; }
    const name = profileName.trim().toLowerCase();
    const match = characters.find(c =>
      c.name.toLowerCase() === name ||
      c.name.toLowerCase().replace(/[^a-z]/g, "") === name.replace(/[^a-z]/g, "")
    );
    setMatchedChar(match || null);
  }, [profileName, characters]);

  const updateBlend = (key, val) => setBlend(prev => ({ ...prev, [key]: val }));

  const applyBlendPreset = (preset) => {
    setSelectedBlendPreset(preset.id);
    setBlend({ ...preset.values });
  };

  const applyDialectPreset = (key) => {
    if (!presets?.dialect_presets?.[key]) return;
    const p = presets.dialect_presets[key];
    setSelectedDialect(key);
    setGender(p.gender);
    setBaseVoice(p.base_voice);
    setOctaveRange(p.octave_range);
    setSelectedRules(p.grammar_rules || []);
    setEmotionPreset(p.emotion_base || "neutral_professional");
    // Apply TTS params into blend
    setBlend(prev => ({
      ...prev,
      speed: p.tts_params.speed,
      pitch: p.tts_params.pitch_shift,
      breathiness: Math.round((p.audio_fx.breathiness || 0) * 100),
    }));
    if (!profileName) setProfileName(p.label);
  };

  const loadProfile = (profile) => {
    setEditing(profile.id);
    setProfileName(profile.name);
    setSelectedDialect(profile.dialect_preset || "");
    setGender(profile.gender);
    setBaseVoice(profile.base_voice);
    setOctaveRange(profile.octave_range || "baritone");
    setSelectedRules(profile.grammar_rules || []);
    setEmotionPreset(profile.emotion_preset || "neutral_professional");
    setSelectedBlendPreset(profile.blend_preset_id || "");
    // Load all blend params
    const b = profile.voice_blend || {};
    setBlend({
      formantShift: b.formantShift ?? 0, brightness: b.brightness ?? 0, bodyWeight: b.bodyWeight ?? 0,
      speed: b.speed ?? 1.0, emphasis: b.emphasis ?? 0, clarity: b.clarity ?? 0,
      sibilance: b.sibilance ?? 0, pitch: b.pitch ?? 0, breathiness: b.breathiness ?? 0,
      raspiness: b.raspiness ?? 0, compression: b.compression ?? 0, volume: b.volume ?? 100,
    });
  };

  const resetForm = () => {
    stopPlayback();
    setEditing(null); setProfileName(""); setSelectedDialect("");
    setGender("male"); setBaseVoice("ash"); setOctaveRange("baritone");
    setSelectedRules([]); setEmotionPreset("neutral_professional");
    setSelectedBlendPreset(""); setBlend({ ...BLEND_DEFAULTS });
    setDecodedBuffer(null); setPreviewAudioUrl(null);
  };

  const toggleRule = (rule) => setSelectedRules(prev => prev.includes(rule) ? prev.filter(r => r !== rule) : [...prev, rule]);

  // ─── Web Audio API: Build effects chain from blend params ───
  const buildBlendGraph = useCallback((ctx, buffer, blendParams) => {
    const b = blendParams;
    const source = ctx.createBufferSource();
    source.buffer = buffer;

    // Pitch + Speed via playbackRate
    const pitchRate = Math.pow(2, b.pitch / 12);
    source.playbackRate.value = pitchRate * b.speed;

    // Low shelf (body/weight) — boosted range
    const lowShelf = ctx.createBiquadFilter();
    lowShelf.type = "lowshelf"; lowShelf.frequency.value = 250; lowShelf.gain.value = b.bodyWeight * 1.5;

    // Formant shaping (2 peaking filters) — stronger effect
    const formant1 = ctx.createBiquadFilter();
    formant1.type = "peaking"; formant1.frequency.value = 1000 + b.formantShift * 120; formant1.Q.value = 2.0;
    formant1.gain.value = Math.abs(b.formantShift) > 0 ? 6 : 0;

    const formant2 = ctx.createBiquadFilter();
    formant2.type = "peaking"; formant2.frequency.value = 2800 + b.formantShift * 200; formant2.Q.value = 2.0;
    formant2.gain.value = Math.abs(b.formantShift) > 0 ? 5 : 0;

    // Presence / Clarity — boosted
    const presence = ctx.createBiquadFilter();
    presence.type = "peaking"; presence.frequency.value = 3500; presence.Q.value = 1.0; presence.gain.value = b.clarity * 1.5;

    // Sibilance — boosted
    const sibilanceFilter = ctx.createBiquadFilter();
    sibilanceFilter.type = "peaking"; sibilanceFilter.frequency.value = 7000; sibilanceFilter.Q.value = 2.0; sibilanceFilter.gain.value = b.sibilance * 1.5;

    // Brightness (high shelf) — boosted
    const highShelf = ctx.createBiquadFilter();
    highShelf.type = "highshelf"; highShelf.frequency.value = 5000; highShelf.gain.value = b.brightness * 1.5;

    // Compression + emphasis
    const compressor = ctx.createDynamicsCompressor();
    const compAmt = Math.max(b.compression, b.emphasis);
    compressor.threshold.value = -50 + (100 - compAmt) * 0.4;
    compressor.ratio.value = 1 + compAmt * 0.15;
    compressor.knee.value = 10; compressor.attack.value = 0.003; compressor.release.value = 0.25;

    // Raspiness (waveshaper distortion) — amplified for TTS audio
    const waveshaper = ctx.createWaveShaper();
    if (b.raspiness > 0) {
      const amt = (b.raspiness / 100) * 3;  // 3x amplification for audible effect
      const n = 44100;
      const curve = new Float32Array(n);
      for (let i = 0; i < n; i++) { const x = (i * 2) / n - 1; curve[i] = ((1 + amt) * x) / (1 + amt * Math.abs(x)); }
      waveshaper.curve = curve; waveshaper.oversample = "2x";
    }

    // Volume
    const gainNode = ctx.createGain();
    gainNode.gain.value = b.volume / 100;

    // Breathiness (shaped noise layer) — boosted for clarity
    let noiseGain = null, noiseSource = null;
    if (b.breathiness > 0) {
      const noiseLen = buffer.length;
      const noiseBuf = ctx.createBuffer(1, noiseLen, buffer.sampleRate);
      const noiseData = noiseBuf.getChannelData(0);
      for (let i = 0; i < noiseLen; i++) noiseData[i] = (Math.random() * 2 - 1) * 0.2;
      noiseSource = ctx.createBufferSource(); noiseSource.buffer = noiseBuf;
      const noiseHP = ctx.createBiquadFilter();
      noiseHP.type = "highpass"; noiseHP.frequency.value = 400; noiseHP.Q.value = 0.7;
      const noiseLP = ctx.createBiquadFilter();
      noiseLP.type = "lowpass"; noiseLP.frequency.value = 3500; noiseLP.Q.value = 0.7;
      const breathPeak = ctx.createBiquadFilter();
      breathPeak.type = "peaking"; breathPeak.frequency.value = 1600; breathPeak.Q.value = 0.8; breathPeak.gain.value = 4;
      noiseGain = ctx.createGain(); noiseGain.gain.value = b.breathiness / 150;  // boosted from /500
      noiseSource.connect(noiseHP); noiseHP.connect(noiseLP); noiseLP.connect(breathPeak); breathPeak.connect(noiseGain);
    }

    // Wire the chain
    source.connect(lowShelf); lowShelf.connect(formant1); formant1.connect(formant2);
    formant2.connect(presence); presence.connect(sibilanceFilter); sibilanceFilter.connect(highShelf);
    highShelf.connect(compressor); compressor.connect(waveshaper); waveshaper.connect(gainNode);
    gainNode.connect(ctx.destination);
    if (noiseGain) noiseGain.connect(ctx.destination);

    return { source, noiseSource };
  }, []);

  const stopPlayback = useCallback(() => {
    try { sourceNodeRef.current?.stop(); } catch {}
    try { noiseNodeRef.current?.stop(); } catch {}
    try { audioCtxRef.current?.close(); } catch {}
    sourceNodeRef.current = null; noiseNodeRef.current = null; audioCtxRef.current = null;
    setPlayingProfile(null);
  }, []);

  // Cleanup on unmount
  useEffect(() => () => stopPlayback(), [stopPlayback]);

  const playRawAudio = useCallback((buffer) => {
    stopPlayback();
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    audioCtxRef.current = ctx;
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    sourceNodeRef.current = source;
    source.onended = () => setPlayingProfile(null);
    source.start(0);
    setPlayingProfile("raw");
  }, [stopPlayback]);

  const playBlendedAudio = useCallback((buffer) => {
    stopPlayback();
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    audioCtxRef.current = ctx;
    const { source, noiseSource } = buildBlendGraph(ctx, buffer, blend);
    sourceNodeRef.current = source;
    noiseNodeRef.current = noiseSource;
    source.onended = () => setPlayingProfile(null);
    source.start(0);
    if (noiseSource) noiseSource.start(0);
    setPlayingProfile("preview");
  }, [blend, buildBlendGraph, stopPlayback]);

  const previewVoice = async () => {
    stopPlayback();
    setPreviewing(true);
    try {
      const res = await axios.post(`${API}/voice-lab/preview`, {
        dialect_preset: selectedDialect || null,
        base_voice: baseVoice, speed: blend.speed, text: sampleText,
        grammar_rules: selectedRules, prosody: {},
      });
      setTransformedText(res.data.text_transformed);

      // Fetch + decode audio
      const audioUrl = `${BACKEND_URL}${res.data.audio_url}`;
      setPreviewAudioUrl(audioUrl);
      const resp = await fetch(audioUrl);
      const arrayBuf = await resp.arrayBuffer();
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const decoded = await ctx.decodeAudioData(arrayBuf);
      await ctx.close();
      setDecodedBuffer(decoded);
      // Play RAW — no blend effects, so user can compare with "Replay with Blend"
      playRawAudio(decoded);
    } catch (e) { console.error(e); alert("Preview failed — check Emergent LLM key balance."); }
    setPreviewing(false);
  };

  const saveProfile = async () => {
    if (!profileName.trim()) return alert("Profile name required");
    setSaving(true);
    const payload = {
      name: profileName, series: series || "the-red-ink-chronicles",
      gender, dialect_preset: selectedDialect || null,
      base_voice: baseVoice, octave_range: octaveRange,
      tts_params: { speed: blend.speed, pitch_shift: blend.pitch },
      audio_fx: { breathiness: blend.breathiness / 100, warmth: 0.4, whisper_blend: 0 },
      voice_blend: { ...blend },
      blend_preset_id: selectedBlendPreset || null,
      prosody: {}, grammar_rules: selectedRules, regional_markers: [],
      emotion_preset: emotionPreset, voice_blend_preset: selectedBlendPreset || null,
      character_id: matchedChar?.id || null,
      description: `${profileName} — ${selectedDialect || "custom"} voice profile`,
    };
    try {
      if (editing) {
        try {
          await axios.put(`${API}/voice-lab/profiles/${editing}`, payload);
        } catch (updateErr) {
          // If profile was deleted (e.g. after re-seed), create a new one instead
          if (updateErr?.response?.status === 404) {
            const res = await axios.post(`${API}/voice-lab/profiles`, payload);
            setEditing(res.data.id);
          } else {
            throw updateErr;
          }
        }
      } else {
        const res = await axios.post(`${API}/voice-lab/profiles`, payload);
        setEditing(res.data.id);
      }
      // Auto-assign if name matches a character
      if (matchedChar) {
        const profileId = editing || (await axios.get(`${API}/voice-lab/profiles?series=${series || ""}`)).data.find(p => p.name === profileName)?.id;
        if (profileId) {
          await axios.post(`${API}/voice-lab/profiles/${profileId}/assign/${matchedChar.id}`);
        }
      }
      const res = await axios.get(`${API}/voice-lab/profiles?series=${series || ""}`);
      setProfiles(res.data);
    } catch (e) { console.error(e); alert("Save failed: " + (e?.response?.data?.detail || e?.message || "Unknown error")); }
    setSaving(false);
  };

  const deleteProfile = async (id) => {
    if (!window.confirm("Delete this voice profile?")) return;
    await axios.delete(`${API}/voice-lab/profiles/${id}`);
    setProfiles(prev => prev.filter(p => p.id !== id));
    if (editing === id) resetForm();
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="animate-spin text-amber-500" /></div>;

  const dialectsByGender = presets?.dialect_presets
    ? Object.entries(presets.dialect_presets).reduce((acc, [k, v]) => {
        if (!acc[v.gender]) acc[v.gender] = [];
        acc[v.gender].push({ key: k, ...v });
        return acc;
      }, {})
    : {};

  return (
    <div className="space-y-5" data-testid="voice-lab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Mic size={20} className="text-amber-500" />
          <h2 className="font-cinzel text-lg font-bold">Voice Lab</h2>
          <span className="text-xs text-gray-500">{profiles.length} profiles</span>
        </div>
        <button onClick={resetForm} className="flex items-center gap-1 text-xs text-gray-400 hover:text-white px-3 py-1.5 bg-white/5 rounded transition-colors">
          <RefreshCw size={12} /> New Profile
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* ====== LEFT: Profile Editor ====== */}
        <div className="lg:col-span-2 space-y-5">

          {/* Profile Name + Auto-match indicator */}
          <div>
            <input type="text" value={profileName} onChange={e => setProfileName(e.target.value)}
              placeholder="Character name (e.g., 'LEILAN', 'Vanessa', 'Notary Hill')"
              className="w-full px-4 py-3 bg-[#0a0a0a] border border-white/10 rounded-lg text-white font-cinzel focus:outline-none focus:border-amber-500"
              data-testid="voice-lab-name" />
            {matchedChar && (
              <div className="flex items-center gap-2 mt-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded text-xs text-emerald-400">
                <UserCheck size={12} /> Will auto-assign to <strong>{matchedChar.name}</strong>
              </div>
            )}
          </div>

          {/* Dialect Presets */}
          <div>
            <h3 className="text-xs text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Sliders size={12} /> Regional Dialect
            </h3>
            {["male", "female"].map(g => (
              <div key={g} className="mb-2">
                <p className="text-[10px] font-bold text-gray-600 uppercase mb-1.5">{g}</p>
                <div className="flex flex-wrap gap-1.5">
                  {(dialectsByGender[g] || []).map(d => (
                    <button key={d.key} onClick={() => applyDialectPreset(d.key)}
                      className={`px-2.5 py-1 rounded text-[11px] border transition-all ${
                        selectedDialect === d.key ? "bg-amber-500/20 border-amber-500/40 text-amber-400" : "bg-white/5 border-white/10 text-gray-400 hover:border-white/20"
                      }`} data-testid={`dialect-${d.key}`}>
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Base Voice + Octave */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-gray-500 uppercase block mb-1">Base Voice</label>
              <select value={baseVoice} onChange={e => setBaseVoice(e.target.value)}
                className="w-full px-2 py-2 bg-[#0a0a0a] border border-white/10 rounded text-sm text-white" data-testid="voice-lab-base-voice">
                {presets?.base_voices?.map(v => <option key={v.value} value={v.value}>{v.label} ({v.gender})</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] text-gray-500 uppercase block mb-1">Octave Range</label>
              <select value={octaveRange} onChange={e => setOctaveRange(e.target.value)}
                className="w-full px-2 py-2 bg-[#0a0a0a] border border-white/10 rounded text-sm text-white">
                <option value="bass">Bass (Deep Male)</option>
                <option value="baritone">Baritone (Male)</option>
                <option value="baritone_light">Light Baritone</option>
                <option value="tenor">Tenor (High Male)</option>
                <option value="alto">Alto (Low Female)</option>
                <option value="mezzo_soprano">Mezzo Soprano</option>
                <option value="soprano">Soprano (High Female)</option>
              </select>
            </div>
          </div>

          {/* ═══════ VOICE BLEND SECTION ═══════ */}
          <div>
            <h3 className="text-xs text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Zap size={12} /> Voice Blend
            </h3>
            {/* Blend Presets */}
            <div className="flex flex-wrap gap-1.5 mb-4">
              {BLEND_PRESETS.map(p => (
                <button key={p.id} onClick={() => applyBlendPreset(p)}
                  className={`px-2.5 py-1.5 rounded-lg text-[11px] border transition-all ${
                    selectedBlendPreset === p.id ? "border-opacity-60 text-white" : "bg-white/5 border-white/10 text-gray-400 hover:text-white"
                  }`}
                  style={selectedBlendPreset === p.id ? { backgroundColor: p.color + "25", borderColor: p.color + "60", color: p.color } : {}}
                  data-testid={`blend-preset-${p.id}`}>
                  {p.label}
                </button>
              ))}
            </div>

            {/* Full Voice Blend Controls Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Section title="Timbre" color="#FF6B6B">
                <Knob label="Formant Shift" value={blend.formantShift} onChange={v => updateBlend("formantShift", v)} min={-12} max={12} step={1} unit="st" color="#FF6B6B" />
                <Knob label="Brightness" value={blend.brightness} onChange={v => updateBlend("brightness", v)} min={-10} max={10} step={1} unit="dB" color="#FF6B6B" />
                <Knob label="Body / Weight" value={blend.bodyWeight} onChange={v => updateBlend("bodyWeight", v)} min={-10} max={10} step={1} unit="dB" color="#FF6B6B" />
              </Section>

              <Section title="Prosody" color="#38BDF8">
                <Knob label="Speed" value={blend.speed} onChange={v => updateBlend("speed", v)} min={0.5} max={2.0} step={0.01} unit="x" color="#38BDF8" />
                <Knob label="Emphasis" value={blend.emphasis} onChange={v => updateBlend("emphasis", v)} min={0} max={100} step={1} unit="%" color="#38BDF8" />
              </Section>

              <Section title="Articulation" color="#F59E0B">
                <Knob label="Presence / Clarity" value={blend.clarity} onChange={v => updateBlend("clarity", v)} min={-10} max={10} step={1} unit="dB" color="#F59E0B" />
                <Knob label="Sibilance" value={blend.sibilance} onChange={v => updateBlend("sibilance", v)} min={-10} max={10} step={1} unit="dB" color="#F59E0B" />
              </Section>

              <Section title="Pitch" color="#A78BFA">
                <Knob label="Pitch Shift" value={blend.pitch} onChange={v => updateBlend("pitch", v)} min={-12} max={12} step={1} unit="st" color="#A78BFA" />
              </Section>

              <Section title="Texture" color="#EC4899">
                <Knob label="Breathiness" value={blend.breathiness} onChange={v => updateBlend("breathiness", v)} min={0} max={100} step={1} unit="%" color="#EC4899" />
                <Knob label="Raspiness" value={blend.raspiness} onChange={v => updateBlend("raspiness", v)} min={0} max={100} step={1} unit="%" color="#EC4899" />
              </Section>

              <Section title="Dynamics" color="#1ABC9C">
                <Knob label="Compression" value={blend.compression} onChange={v => updateBlend("compression", v)} min={0} max={100} step={1} unit="%" color="#1ABC9C" />
                <Knob label="Volume" value={blend.volume} onChange={v => updateBlend("volume", v)} min={50} max={150} step={1} unit="%" color="#1ABC9C" />
              </Section>
            </div>
          </div>

          {/* Grammar Rules */}
          <div>
            <h3 className="text-xs text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2">
              <Wand2 size={12} /> Grammar Rules (AAVE)
            </h3>
            <div className="flex flex-wrap gap-1.5">
              {presets?.grammar_rules && Object.entries(presets.grammar_rules).map(([key, rule]) => (
                <button key={key} onClick={() => toggleRule(key)}
                  className={`px-2.5 py-1 rounded text-[11px] border transition-all ${
                    selectedRules.includes(key) ? "bg-red-500/20 border-red-500/40 text-red-400" : "bg-white/5 border-white/10 text-gray-400 hover:border-white/20"
                  }`} title={rule.description} data-testid={`grammar-${key}`}>
                  {rule.label}
                </button>
              ))}
            </div>
          </div>

          {/* Emotion Preset */}
          <div>
            <h3 className="text-xs text-gray-500 uppercase tracking-wider mb-2">Emotion</h3>
            <div className="flex flex-wrap gap-1.5">
              {presets?.emotion_presets && Object.entries(presets.emotion_presets).map(([key, ep]) => (
                <button key={key} onClick={() => setEmotionPreset(key)}
                  className={`px-2.5 py-1 rounded text-[11px] border transition-all ${
                    emotionPreset === key ? "bg-blue-500/20 border-blue-500/40 text-blue-400" : "bg-white/5 border-white/10 text-gray-400 hover:border-white/20"
                  }`} data-testid={`emotion-${key}`}>
                  {ep.label}
                </button>
              ))}
            </div>
          </div>

          {/* Preview & Save */}
          <div className="p-4 bg-[#0a0a0a] rounded-xl border border-white/10 space-y-3">
            <label className="text-[10px] text-gray-500 uppercase block">Preview Text</label>
            <textarea value={sampleText} onChange={e => setSampleText(e.target.value)} rows={2}
              className="w-full bg-transparent border border-white/10 rounded p-3 text-sm text-gray-300 resize-none focus:outline-none focus:border-amber-500"
              data-testid="voice-lab-sample-text" />
            {transformedText && transformedText !== sampleText && (
              <div className="p-2 bg-red-500/5 rounded border border-red-500/10">
                <p className="text-[10px] text-red-400 uppercase mb-1">Grammar Transform:</p>
                <p className="text-xs text-gray-300 italic">{transformedText}</p>
              </div>
            )}
            <div className="flex flex-wrap gap-3">
              <button onClick={previewVoice} disabled={previewing}
                className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-black font-bold text-xs rounded-lg transition-colors disabled:opacity-50"
                data-testid="voice-lab-preview-btn">
                {previewing ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
                {previewing ? "Generating..." : "Preview Raw Voice"}
              </button>
              {playingProfile && (
                <button onClick={stopPlayback}
                  className="flex items-center gap-2 px-4 py-2.5 bg-red-500/15 hover:bg-red-500/25 text-red-400 text-xs font-bold rounded-lg border border-red-500/20 transition-colors"
                  data-testid="voice-lab-stop-btn">
                  <Square size={12} /> Stop
                </button>
              )}
              {decodedBuffer && !playingProfile && (
                <>
                  <button onClick={() => playBlendedAudio(decodedBuffer)}
                    className="flex items-center gap-2 px-4 py-2.5 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-xs font-bold rounded-lg border border-emerald-500/20 transition-colors"
                    data-testid="voice-lab-replay-blend-btn">
                    <Sliders size={12} /> Play with Blend
                  </button>
                  <button onClick={() => playRawAudio(decodedBuffer)}
                    className="flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 text-white/50 text-xs font-bold rounded-lg border border-white/10 transition-colors"
                    data-testid="voice-lab-replay-raw-btn">
                    <Play size={12} /> Play Raw
                  </button>
                </>
              )}
              <button onClick={saveProfile} disabled={saving}
                className="flex items-center gap-2 px-5 py-2.5 bg-white/5 hover:bg-white/10 text-white text-xs font-bold rounded-lg border border-white/10 transition-colors disabled:opacity-50"
                data-testid="voice-lab-save-btn">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {editing ? "Update Profile" : "Save Profile"}
              </button>
            </div>
            {decodedBuffer && (
              <p className="text-[10px] text-emerald-400/60 mt-1">
                Click "Preview Raw Voice" to hear the base TTS. Click "Play with Blend" to hear it with your blend effects applied.
              </p>
            )}
          </div>
        </div>

        {/* ====== RIGHT: Saved Profiles ====== */}
        <div className="space-y-3">
          <h3 className="text-xs text-gray-500 uppercase tracking-wider">Saved Profiles</h3>
          {profiles.length === 0 && (
            <p className="text-gray-600 text-xs text-center py-8">No profiles yet. Name it after a character to auto-assign.</p>
          )}
          {profiles.map(profile => {
            const assignedChar = characters.find(c => c.id === profile.character_id);
            return (
              <div key={profile.id}
                className={`p-3 rounded-lg border transition-all cursor-pointer ${
                  editing === profile.id ? "border-amber-500/40 bg-amber-500/5" : "border-white/8 bg-white/[0.02] hover:border-white/15"
                }`}
                onClick={() => loadProfile(profile)}
                data-testid={`profile-card-${profile.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <h4 className="font-cinzel text-sm font-bold text-white truncate">{profile.name}</h4>
                  <div className="flex gap-1">
                    <button onClick={(e) => { e.stopPropagation(); deleteProfile(profile.id); }}
                      className="p-1 text-gray-600 hover:text-red-400 transition-colors">
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mb-1">
                  <span className="text-[10px] px-1.5 py-0.5 bg-white/5 rounded text-gray-400">{profile.gender}</span>
                  <span className="text-[10px] px-1.5 py-0.5 bg-blue-500/10 rounded text-blue-400">{profile.base_voice}</span>
                  {profile.blend_preset_id && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-purple-500/10 rounded text-purple-400">{profile.blend_preset_id}</span>
                  )}
                  {profile.dialect_preset && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-amber-500/10 rounded text-amber-400">
                      {presets?.dialect_presets?.[profile.dialect_preset]?.label || profile.dialect_preset}
                    </span>
                  )}
                </div>
                {assignedChar && (
                  <div className="flex items-center gap-1 text-[10px] text-emerald-400">
                    <UserCheck size={10} /> Assigned to {assignedChar.name}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
